/**
 * Voice-mode controls shown above the composer: the "Tap to talk" start button (also the gesture
 * that unlocks audio on iOS), the live state with a microphone level meter, interrupt and
 * "I'm done" actions, the sensitivity choice, an explicit Stop, and the resume prompt after the
 * page was in the background. With `?voiceDebug=1` the page also renders
 * {@link FluxAgentVoiceDebug}, a latency overlay.
 *
 * @module apps/web/src/components/flux-agent/FluxAgentVoicePanel
 */

import { AudioLines, Hand, Loader2, Mic, MicOff, Pause, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FluxAgentVoiceMode, VoicePhase } from './useFluxAgentVoice';
import { VOICE_SENSITIVITIES, type VoiceSensitivity } from '@/lib/fluxAgentVoice';
import { cn } from '@/lib/utils';

/** Caption for each running state. */
export const VOICE_LABELS: Record<VoicePhase, string> = {
  off: '',
  starting: 'Starting the microphone…',
  listening: 'Listening — just start talking',
  hearing: 'Hearing you…',
  transcribing: 'Catching that…',
  thinking: 'Thinking… speak to interrupt',
  speaking: 'Speaking… speak to interrupt',
  paused: 'Voice paused while Flux Agent was in the background',
};

/** Display names for the sensitivity choices. */
const SENSITIVITY_LABELS: Record<VoiceSensitivity, string> = {
  low: 'Low (noisy room)',
  medium: 'Medium',
  high: 'High (quiet voice)',
};

/** Inputs for {@link FluxAgentVoicePanel}. */
export interface FluxAgentVoicePanelProps {
  /** Voice-mode state and actions from `useFluxAgentVoiceMode`. */
  voice: FluxAgentVoiceMode;
  /** Show the large "Tap to talk" prompt while voice mode is off (deep link `?voice=1`). */
  ready: boolean;
  /** Dismiss the ready prompt without starting. */
  onDismissReady: () => void;
}

/** Render the voice-mode controls, or nothing when voice mode is off, not armed and error-free. */
export function FluxAgentVoicePanel({ voice, ready, onDismissReady }: FluxAgentVoicePanelProps) {
  const { phase } = voice;
  const active = phase !== 'off';

  if (!active && ready) {
    return (
      <div
        className="border-primary/30 bg-primary/5 flex flex-col items-center gap-3 rounded-xl border px-4 py-5 text-center"
        data-voice-state="ready"
      >
        <button
          type="button"
          onClick={voice.start}
          className="bg-primary text-primary-foreground focus-visible:ring-ring flex h-20 w-20 items-center justify-center rounded-full shadow-lg transition-transform focus-visible:outline-none focus-visible:ring-4 active:scale-95"
          aria-label="Tap to talk"
        >
          <Mic className="h-8 w-8" />
        </button>
        <div className="space-y-1">
          <p className="font-medium">Tap to talk</p>
          <p className="text-muted-foreground max-w-xs text-sm">
            Talk hands-free. Flux Agent listens, answers out loud, and stops when you start
            speaking.
          </p>
        </div>
        {voice.error && (
          <p role="alert" className="text-destructive text-sm">
            {voice.error}
          </p>
        )}
        <Button type="button" variant="ghost" size="sm" onClick={onDismissReady}>
          Type instead
        </Button>
      </div>
    );
  }

  if (!active) {
    return voice.error ? (
      <div
        role="alert"
        className="border-destructive/40 bg-destructive/5 text-destructive rounded-lg border px-3 py-2 text-sm"
      >
        {voice.error}
      </div>
    ) : null;
  }

  const busy = phase === 'starting' || phase === 'transcribing';
  const agentTurn = phase === 'thinking' || phase === 'speaking';
  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-lg border px-3 py-2 text-sm',
        phase === 'paused' ? 'border-amber-500/40 bg-amber-500/5' : 'border-primary/30 bg-primary/5'
      )}
      data-voice-state={phase}
      aria-live="polite"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="relative flex h-7 w-7 items-center justify-center" aria-hidden="true">
          <span
            className={cn(
              'absolute inset-0 rounded-full transition-transform duration-75',
              phase === 'hearing' ? 'bg-primary/40' : 'bg-primary/20'
            )}
            style={{
              transform: `scale(${phase === 'listening' || phase === 'hearing' ? 0.55 + Math.min(1, voice.level) * 0.6 : phase === 'speaking' ? 1 : 0.6})`,
            }}
          />
          {phase === 'paused' ? (
            <Pause className="relative h-3.5 w-3.5 text-amber-600" />
          ) : busy || phase === 'thinking' ? (
            <Loader2 className="text-primary relative h-3.5 w-3.5 animate-spin" />
          ) : phase === 'speaking' ? (
            <AudioLines className="text-primary relative h-3.5 w-3.5" />
          ) : (
            <Mic className="text-primary relative h-3.5 w-3.5" />
          )}
        </span>
        <span className="min-w-0 flex-1 font-medium">{VOICE_LABELS[phase]}</span>
        {phase === 'paused' ? (
          <Button type="button" size="sm" className="h-8 gap-1" onClick={voice.resume}>
            <Mic className="h-3.5 w-3.5" /> Tap to resume
          </Button>
        ) : phase === 'hearing' ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8"
            onClick={voice.finishUtterance}
          >
            I’m done
          </Button>
        ) : agentTurn ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1"
            onClick={voice.interrupt}
          >
            <Hand className="h-3.5 w-3.5" /> Interrupt
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant="destructive"
          className="h-8 gap-1"
          onClick={voice.stop}
        >
          <Square className="h-3 w-3 fill-current" /> Stop
        </Button>
      </div>
      {phase !== 'paused' && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div
            className="bg-muted relative h-1.5 min-w-[6rem] flex-1 overflow-hidden rounded-full"
            role="meter"
            aria-label="Microphone level"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(Math.min(1, voice.level) * 100)}
          >
            <div
              className={cn(
                'absolute inset-y-0 left-0 rounded-full transition-[width] duration-75',
                phase === 'hearing' ? 'bg-primary' : 'bg-primary/50'
              )}
              style={{ width: `${Math.round(Math.min(1, voice.level) * 100)}%` }}
            />
          </div>
          <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
            <MicOff className="h-3 w-3" aria-hidden="true" />
            Sensitivity
            <select
              className="bg-background rounded border px-1.5 py-0.5 text-xs"
              value={voice.sensitivity}
              onChange={event => voice.setSensitivity(event.target.value as VoiceSensitivity)}
            >
              {VOICE_SENSITIVITIES.map(value => (
                <option key={value} value={value}>
                  {SENSITIVITY_LABELS[value]}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {voice.error && (
        <p role="alert" className="text-destructive text-xs">
          {voice.error}
        </p>
      )}
    </div>
  );
}

/** Inputs for {@link FluxAgentVoiceDebug}. */
export interface FluxAgentVoiceDebugProps {
  /** Voice-mode state, including recent turn timings. */
  voice: FluxAgentVoiceMode;
}

/**
 * Developer latency overlay (`?voiceDebug=1`): engine state, playback, and per-hop timings of
 * recent turns in milliseconds. Shows numbers only, never audio or text.
 */
export function FluxAgentVoiceDebug({ voice }: FluxAgentVoiceDebugProps) {
  const format = (value: number | null) => (value === null ? '—' : `${value}`);
  return (
    <div
      className="bg-background/95 fixed bottom-3 right-3 z-50 w-72 rounded-lg border p-2 font-mono text-[11px] shadow-lg"
      data-testid="voice-debug"
    >
      <p>
        state: <span data-testid="voice-debug-state">{voice.phase}</span> · playing:{' '}
        <span data-testid="voice-debug-playing">{voice.playing ? 'yes' : 'no'}</span> · level{' '}
        {voice.level.toFixed(2)}
      </p>
      <table className="mt-1 w-full text-right">
        <thead className="text-muted-foreground">
          <tr>
            <th className="text-left">turn</th>
            <th title="silence waited before end of speech">endpt</th>
            <th title="end of speech to transcript">stt</th>
            <th title="transcript to first delta">llm</th>
            <th title="first delta to first audio">tts</th>
            <th title="stopped talking to first audio">total</th>
          </tr>
        </thead>
        <tbody>
          {voice.timings.map((timing, index) => (
            <tr key={index}>
              <td className="text-left">-{index}</td>
              <td>{format(timing.endpointing)}</td>
              <td>{format(timing.transcribe)}</td>
              <td>{format(timing.firstDelta)}</td>
              <td>{format(timing.firstAudio)}</td>
              <td>{format(timing.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
